import type { Socket } from 'socket.io-client'
import type { RealtimeConfig } from './realtime'
import { tabSession } from './realtime'

/**
 * LEGATURA CU SERVERUL DE SOCKET - una singura pentru toata aplicatia.
 *
 * Perechea veche e `IOSocket` din `public/js/general.js:2715-2815`, pornita o
 * data in `general.js:2869` si tinuta intr-o variabila globala (`ioSocket`)
 * pentru toate ecranele. Aici e acelasi lucru, dar numarat: cine are nevoie de
 * ea o cere cu `acquirePortalLink`, si cine pleaca de pe ecran o da inapoi.
 * Cand n-o mai vrea nimeni, se inchide.
 *
 * DE CE NUMARATA SI NU GLOBALA. In portal, o pagina intreaga se schimba cu alta
 * si conexiunea moare odata cu ea. In v4 nu se reincarca nimic: un ecran inchis
 * care lasa in urma o conexiune deschisa si un `setInterval` care cere din trei
 * in trei secunde nu se vede la unul singur - se vede in ziua cu multi oameni,
 * cand serverul de socket tine cateva mii de conexiuni care nu se uita nimeni
 * la ele.
 *
 * CALEA E A PORTALULUI, NU ALTA
 * -----------------------------------------------------------------------------
 * Aceeasi in fiecare pas, verificata in codul serverului
 * (`socket.optimumdesk.com/src/controllers/PortalController.js`):
 *
 *  1. `io(<adresa> + '/portal', {transports: ['websocket']})`. NUMAI websocket:
 *     serverul nu are configurat `cors` pentru socket.io, deci stransul de mana
 *     prin polling (care e o cerere HTTP obisnuita) e oprit de browser cand
 *     adresa e a altui domeniu. Prin websocket nu se trece prin CORS.
 *  2. la `connect` - SI la fiecare reconectare - `emit('portal_connected', <sir>)`.
 *     Un SIR SIMPLU, nu un obiect: serverul il baga direct in cheia Redis
 *     `user:<sir>`, iar un obiect ar deveni "[object Object]" pentru toata lumea.
 *  3. serverul raspunde `authenticated` = 'success'. ABIA DUPA el se inregistreaza
 *     acolo handlerele de business - cerut inainte, `performance` se pierde in
 *     tacere. De-aia se asteapta.
 *  4. `emit('<eveniment>', {uSession, _iDevice})` si `on('<eveniment>')` pentru
 *     raspuns.
 *
 * CE NU SPUNE PROTOCOLUL
 * -----------------------------------------------------------------------------
 * Raspunsul care se intoarce e chiar `response`-ul pus de masina, si NU poarta
 * in el numarul masinii. Doua ecrane care cer deodata `performance` pentru doua
 * masini diferite ar primi doua raspunsuri de nedeosebit.
 *
 * Portalul avea aceeasi gaura si a rezolvat-o prin faptul ca se uita la o
 * singura masina o data (`window.performanceInterval`, unul singur, global).
 * Aici raspunsul se da abonatilor masinii pentru care s-a cerut ULTIMA OARA
 * evenimentul ala - si atat. Scris ca sa se vada, nu ascuns: cine deschide doua
 * panouri vii pe doua masini in acelasi timp trebuie sa stie ca serverul nu are
 * cu ce sa le deosebeasca, si ca leacul e un camp in plus in raspunsul masinii,
 * nu inca un strat aici.
 */

/** Evenimentele pe care serverul le duce la masina si le aduce inapoi. */
export type PortalEvent =
  | 'performance'
  | 'processes'
  | 'diskspace'
  | 'connections'
  | 'communications'
  | 'screenshot'
  /** Whois / MX / NS pe domeniul din `email` (Domain Check): singurul cu un camp in plus. */
  | 'domain'

export type PortalStatus =
  /** Nu e configurata nicio adresa - `/api/v2/realtime` a intors `url: null`. */
  | 'unconfigured'
  /** Se conecteaza, sau s-a prezentat si asteapta `authenticated`. */
  | 'connecting'
  /** Se poate cere. */
  | 'ready'
  /** Nu se poate ajunge la server. Clientul incearca mai departe singur. */
  | 'error'

export interface PortalLink {
  status: PortalStatus
  /** Se cheama la fiecare schimbare de stare; intoarce dezabonarea. */
  watch(fn: (status: PortalStatus) => void): () => void
  /** Asculta un eveniment PENTRU O MASINA; intoarce dezabonarea. */
  on(event: PortalEvent, device: number, fn: (payload: unknown) => void): () => void
  /** Cere o data datele. Fara efect cat timp legatura nu e `ready`. `extra` = campuri in plus in cerere (`email` la `domain`). */
  ask(event: PortalEvent, device: number, extra?: Record<string, string>): void
  /** `CHECK_DEVICE`: singurul mod de a afla daca masina e pornita. */
  check(device: number, fn: (online: boolean) => void): void
  /**
   * Camerele scanarilor ADM (`SCAN_WATCH` cu biletul de la
   * `/adm/runs/live/ticket`); `fn` primeste fiecare cadru `SCAN_PROGRESS`,
   * `SCAN_TARGETS`, `SCAN_FINISHED`, `SCAN_WATCHING`, `SCAN_REFUSED`.
   * Intoarce dezabonarea. Fara efect cat timp legatura nu e `ready`.
   */
  scanWatch(ticket: string, fn: (event: ScanEvent, payload: unknown) => void): () => void
  /** Se da inapoi cand ecranul se inchide. Fara ea, conexiunea ramane. */
  release(): void
}

export type ScanEvent = 'SCAN_PROGRESS' | 'SCAN_TARGETS' | 'SCAN_FINISHED' | 'SCAN_WATCHING' | 'SCAN_REFUSED'

const SCAN_EVENTS: ScanEvent[] = ['SCAN_PROGRESS', 'SCAN_TARGETS', 'SCAN_FINISHED', 'SCAN_WATCHING', 'SCAN_REFUSED']

interface Listener {
  event: PortalEvent
  device: number
  fn: (payload: unknown) => void
}

interface Shared {
  key: string
  session: string
  socket: Socket | null
  status: PortalStatus
  users: number
  listeners: Set<Listener>
  watchers: Set<(status: PortalStatus) => void>
  /** Evenimentele pentru care s-a pus deja `socket.on` - o data, nu la fiecare cerere. */
  bound: Set<PortalEvent>
  /** Ultima masina pentru care s-a cerut fiecare eveniment. Vezi capul fisierului. */
  asked: Map<PortalEvent, number>
  /** Inchiderea amanata, ca trecerea de pe o fila pe alta sa nu reconstruiasca tot. */
  closing: ReturnType<typeof setTimeout> | null
}

/**
 * Cat se tine conexiunea dupa ce a plecat si ultimul ecran.
 *
 * Trecerea de pe fila Live pe Health si inapoi ar rupe si ar reface legatura de
 * doua ori in doua secunde, si fiecare refacere costa un strans de mana si o
 * asteptare de `authenticated` - adica exact ecranul gol pe care il repara.
 */
const GRACE_MS = 2000

let shared: Shared | null = null

/**
 * Legatura, pornita daca nu e pornita.
 *
 * `config` vine din `realtimeConfigQuery`. Cand `url` e null se intoarce
 * oricum o legatura, dar in starea `unconfigured`: ecranul are ce spune, si nu
 * se cheama nimic degeaba.
 */
export function acquirePortalLink(config: RealtimeConfig): PortalLink {
  const key = `${config.url ?? ''}${config.namespace}${config.session}`

  if (shared !== null && shared.key !== key) {
    /* Adresa s-a schimbat sub noi (alta configurare, alt om). Cea veche nu mai
       e buna pentru nimeni, deci se inchide acum, nu peste doua secunde. */
    closeNow(shared)
    shared = null
  }

  if (shared === null) {
    shared = {
      key,
      session: tabSession(config.session),
      socket: null,
      status: config.url === null ? 'unconfigured' : 'connecting',
      users: 0,
      listeners: new Set(),
      watchers: new Set(),
      bound: new Set(),
      asked: new Map(),
      closing: null,
    }

    if (config.url !== null) {
      void connect(shared, config)
    }
  }

  const link = shared
  link.users += 1

  if (link.closing !== null) {
    clearTimeout(link.closing)
    link.closing = null
  }

  let given = false

  return {
    get status() {
      return link.status
    },

    watch(fn) {
      link.watchers.add(fn)
      return () => {
        link.watchers.delete(fn)
      }
    },

    on(event, device, fn) {
      const listener: Listener = { event, device, fn }
      link.listeners.add(listener)
      bind(link, event)
      return () => {
        link.listeners.delete(listener)
      }
    },

    ask(event, device, extra) {
      if (link.socket === null || link.status !== 'ready') {
        return
      }
      link.asked.set(event, device)
      link.socket.emit(event, { uSession: link.session, _iDevice: device, ...(extra ?? {}) })
    },

    scanWatch(ticket, fn) {
      const socket = link.socket
      if (socket === null || link.status !== 'ready') {
        return () => undefined
      }
      const handlers = SCAN_EVENTS.map((event) => {
        const handler = (payload: unknown) => fn(event, payload)
        socket.on(event, handler)
        return [event, handler] as const
      })
      socket.emit('SCAN_WATCH', { ticket })
      return () => {
        for (const [event, handler] of handlers) socket.off(event, handler)
      }
    },

    check(device, fn) {
      if (link.socket === null || link.status !== 'ready') {
        fn(false)
        return
      }
      /* INTOTDEAUNA cu raspuns: `CHECK_DEVICE` fara el lasa pe server o
         promisiune fara stapan (`PortalController._check_device`). */
      link.socket.emit('CHECK_DEVICE', { uSession: link.session, _iDevice: device }, (ack: unknown) => {
        fn(ack === 'OK')
      })
    },

    release() {
      /* O data. Un `release()` chemat de doua ori (React in modul strict cheama
         curatarile de doua ori) ar cobori numaratoarea sub zero si ar inchide
         conexiunea altui ecran. */
      if (given) {
        return
      }
      given = true
      link.users -= 1

      if (link.users > 0) {
        return
      }

      link.closing = setTimeout(() => {
        if (link.users === 0) {
          closeNow(link)
          if (shared === link) {
            shared = null
          }
        }
      }, GRACE_MS)
    },
  }
}

async function connect(link: Shared, config: RealtimeConfig): Promise<void> {
  let io: typeof import('socket.io-client').io

  try {
    /* Adus la cerere, nu odata cu aplicatia: clientul de socket e ~40 KB, si
       ecranele care n-au nevoie de timp real nu au de ce sa-l plateasca. */
    ;({ io } = await import('socket.io-client'))
  } catch {
    setStatus(link, 'error')
    return
  }

  /* Legatura poate fi data inapoi cat se aducea pachetul. */
  if (shared !== link) {
    return
  }

  const address = `${(config.url ?? '').replace(/\/+$/, '')}${config.namespace}`

  const socket = io(address, {
    /* Vezi capul fisierului: serverul n-are `cors` pentru socket.io. */
    transports: ['websocket'],
    reconnection: true,
    /* Fara `autoConnect: false`: se conecteaza acum, ca portalul. */
  })

  link.socket = socket

  socket.on('connect', () => {
    /* SIR SIMPLU. Si la FIECARE conectare, nu doar la prima: cheia din Redis
       tine 30 de secunde si e legata de `socket.id`, iar dupa o reconectare
       `socket.id` e altul - fara randul asta, masina raspunde in gol.
       In socket.io v4 `connect` se da si dupa reconectare, deci nu mai trebuie
       ascultat separat `reconnect` cum facea portalul cu clientul v2. */
    socket.emit('portal_connected', link.session)
    setStatus(link, 'connecting')
  })

  socket.on('authenticated', () => {
    setStatus(link, 'ready')
  })

  socket.on('disconnect', () => {
    setStatus(link, 'connecting')
  })

  socket.on('connect_error', () => {
    setStatus(link, 'error')
  })

  /* Evenimentele cerute inainte sa fie gata conexiunea si-au pus deja `on`-ul
     in `bound`; acum e socket pe care sa se puna. */
  for (const event of link.bound) {
    listen(link, event)
  }
}

function bind(link: Shared, event: PortalEvent): void {
  if (link.bound.has(event)) {
    return
  }
  link.bound.add(event)
  listen(link, event)
}

function listen(link: Shared, event: PortalEvent): void {
  const socket = link.socket
  if (socket === null) {
    return
  }

  socket.on(event, (payload: unknown) => {
    const device = link.asked.get(event)
    if (device === undefined) {
      return
    }
    for (const listener of link.listeners) {
      if (listener.event === event && listener.device === device) {
        listener.fn(payload)
      }
    }
  })
}

function setStatus(link: Shared, status: PortalStatus): void {
  if (link.status === status) {
    return
  }
  link.status = status
  for (const watcher of link.watchers) {
    watcher(status)
  }
}

function closeNow(link: Shared): void {
  if (link.closing !== null) {
    clearTimeout(link.closing)
    link.closing = null
  }
  link.socket?.removeAllListeners()
  link.socket?.disconnect()
  link.socket = null
  link.listeners.clear()
  link.watchers.clear()
  link.bound.clear()
  link.asked.clear()
}

/** Numai pentru probe: inchide legatura si uita de ea. */
export function resetPortalLink(): void {
  if (shared !== null) {
    closeNow(shared)
    shared = null
  }
}
